import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Upload as UploadIcon,
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { contractorDetailsAPI } from "../services/api";
import { calculateProfileCompletion } from "../utils/profileCompletion";

// Indian states for compliance
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
];

const businessTypes = [
  { value: "proprietorship", label: "Proprietorship" },
  { value: "partnership", label: "Partnership" },
  { value: "pvt_ltd", label: "Private Limited" },
  { value: "public_ltd", label: "Public Limited" },
  { value: "llp", label: "LLP" },
  { value: "trust", label: "Trust" },
  { value: "society", label: "Society" },
  { value: "other", label: "Other" }
];

const licenseTypes = [
  { value: "principal_employer", label: "Principal Employer" },
  { value: "contractor", label: "Contractor" },
  { value: "sub_contractor", label: "Sub Contractor" }
];

const ContractorProfileWizard = ({ open, onClose, onComplete }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [completionStatus, setCompletionStatus] = useState({
    isComplete: false,
    completedSections: [],
    completionPercentage: 0
  });

  const [formData, setFormData] = useState({
    // Step 1: Company Details
    companyName: "",
    contractorId: "",
    
    // Step 2: Registration Details
    panNumber: "",
    gstNumber: "",
    businessType: "",
    rcNumber: "",
    issueDate: null,
    expiryDate: null,
    issuingAuthority: "",
    validStates: [],
    
    // Step 3: Contact Details
    primaryContactName: "",
    primaryContactDesignation: "",
    primaryContactPhone: "",
    primaryContactEmail: "",
    registeredStreet: "",
    registeredCity: "",
    registeredState: "",
    registeredPincode: "",
    
    // Step 4: Bank Details
    accountName: "",
    accountNumber: "",
    bankName: "",
    ifscCode: "",
    
    // Step 5: Owner Details
    ownerName: "",
    ownerAadhaarNumber: "",
    ownerPanNumber: "",
    ownerPhoneNumber: "",
    ownerEmailAddress: "",
    
    // Step 6: License Details
    laborLicenseNumber: "",
    licenseIssueDate: null,
    licenseValidTill: null,
    licenseType: "",
    maxWorkersAllowed: 0,
    statesApplicable: [],
    
    // Step 7: Documents
    documents: {
      licenseProof: null,
      agreementProof: null,
      gstCertificate: null,
      panCard: null,
      aadhaarCard: null
    }
  });

  const steps = [
    "Company Details",
    "Registration Details", 
    "Contact Details",
    "Bank Details",
    "Owner Details",
    "License Details",
    "Documents"
  ];

  const loadProfileData = useCallback(async () => {
    try {
      setLoading(true);
      // Try to get profile status first
      const response = await contractorDetailsAPI.getProfileStatus();
      if (response.data.success) {
        const data = response.data.data;
        setProfileData(data);
        populateFormData(data);
        calculateCompletionStatus(data);
      } else {
        // Fallback to regular profile endpoint
        const fallbackResponse = await contractorDetailsAPI.getContractorProfile();
        if (fallbackResponse.data.success) {
          const data = fallbackResponse.data.data;
          setProfileData(data);
          populateFormData(data);
          calculateCompletionStatus(data);
        }
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      enqueueSnackbar("Failed to load profile data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (open) {
      loadProfileData();
    }
  }, [open, loadProfileData]);

  const populateFormData = (data) => {
    setFormData({
      companyName: data.companyName || "",
      contractorId: data.contractorId || "",
      panNumber: data.registrationDetails?.panNumber || "",
      gstNumber: data.registrationDetails?.gstNumber || "",
      businessType: data.registrationDetails?.businessType || "",
      rcNumber: data.registrationDetails?.clraDetails?.rcNumber || "",
      issueDate: data.registrationDetails?.clraDetails?.issueDate ? dayjs(data.registrationDetails.clraDetails.issueDate) : null,
      expiryDate: data.registrationDetails?.clraDetails?.expiryDate ? dayjs(data.registrationDetails.clraDetails.expiryDate) : null,
      issuingAuthority: data.registrationDetails?.clraDetails?.issuingAuthority || "",
      validStates: data.registrationDetails?.clraDetails?.validStates || [],
      primaryContactName: data.contactDetails?.primaryContact?.name || "",
      primaryContactDesignation: data.contactDetails?.primaryContact?.designation || "",
      primaryContactPhone: data.contactDetails?.primaryContact?.phone || "",
      primaryContactEmail: data.contactDetails?.primaryContact?.email || "",
      registeredStreet: data.contactDetails?.address?.registered?.street || "",
      registeredCity: data.contactDetails?.address?.registered?.city || "",
      registeredState: data.contactDetails?.address?.registered?.state || "",
      registeredPincode: data.contactDetails?.address?.registered?.pincode || "",
      accountName: data.bankDetails?.accountName || "",
      accountNumber: data.bankDetails?.accountNumber || "",
      bankName: data.bankDetails?.bankName || "",
      ifscCode: data.bankDetails?.ifscCode || "",
      ownerName: data.ownerDetails?.ownerName || "",
      ownerAadhaarNumber: data.ownerDetails?.aadhaarNumber || "",
      ownerPanNumber: data.ownerDetails?.panNumber || "",
      ownerPhoneNumber: data.ownerDetails?.phoneNumber || "",
      ownerEmailAddress: data.ownerDetails?.emailAddress || "",
      laborLicenseNumber: data.licenseDetails?.laborLicenseNumber || "",
      licenseIssueDate: data.licenseDetails?.issueDate ? dayjs(data.licenseDetails.issueDate) : null,
      licenseValidTill: data.licenseDetails?.validTill ? dayjs(data.licenseDetails.validTill) : null,
      licenseType: data.licenseDetails?.licenseType || "",
      maxWorkersAllowed: data.licenseDetails?.maxWorkersAllowed || 0,
      statesApplicable: data.licenseDetails?.statesApplicable || [],
      documents: data.documents || {}
    });
  };

  const calculateCompletionStatus = (data) => {
    const { completionPercentage, isComplete } = calculateProfileCompletion(data);
    
    setCompletionStatus({
      isComplete,
      completedSections: [], // Not needed for display, just for compatibility
      completionPercentage
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDocumentUpload = (documentType, file) => {
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [documentType]: file
      }
    }));
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Prepare payload
      const payload = {
        companyName: formData.companyName,
        contractorId: formData.contractorId,
        registrationDetails: {
          panNumber: formData.panNumber,
          gstNumber: formData.gstNumber,
          businessType: formData.businessType,
          clraDetails: {
            rcNumber: formData.rcNumber,
            issueDate: formData.issueDate ? formData.issueDate.format("YYYY-MM-DD") : null,
            expiryDate: formData.expiryDate ? formData.expiryDate.format("YYYY-MM-DD") : null,
            issuingAuthority: formData.issuingAuthority,
            validStates: formData.validStates
          }
        },
        contactDetails: {
          primaryContact: {
            name: formData.primaryContactName,
            designation: formData.primaryContactDesignation,
            phone: formData.primaryContactPhone,
            email: formData.primaryContactEmail
          },
          address: {
            registered: {
              street: formData.registeredStreet,
              city: formData.registeredCity,
              state: formData.registeredState,
              pincode: formData.registeredPincode
            }
          }
        },
        bankDetails: {
          accountName: formData.accountName,
          accountNumber: formData.accountNumber,
          bankName: formData.bankName,
          ifscCode: formData.ifscCode
        },
        ownerDetails: {
          ownerName: formData.ownerName,
          aadhaarNumber: formData.ownerAadhaarNumber,
          panNumber: formData.ownerPanNumber,
          phoneNumber: formData.ownerPhoneNumber,
          emailAddress: formData.ownerEmailAddress
        },
        licenseDetails: {
          laborLicenseNumber: formData.laborLicenseNumber,
          issueDate: formData.licenseIssueDate ? formData.licenseIssueDate.format("YYYY-MM-DD") : null,
          validTill: formData.licenseValidTill ? formData.licenseValidTill.format("YYYY-MM-DD") : null,
          licenseType: formData.licenseType,
          maxWorkersAllowed: formData.maxWorkersAllowed,
          statesApplicable: formData.statesApplicable
        }
      };

      // Upload documents if any
      if (formData.documents) {
        for (const [docType, file] of Object.entries(formData.documents)) {
          if (file) {
            await contractorDetailsAPI.uploadDocument(profileData._id, file, docType);
          }
        }
      }

      await contractorDetailsAPI.updateContractorProfile(payload);
      
      enqueueSnackbar("Profile updated successfully", { variant: "success" });
      onComplete();
      onClose();
    } catch (error) {
      console.error("Error saving profile:", error);
      enqueueSnackbar("Failed to update profile", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0: // Company Details
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name *"
                value={formData.companyName}
                onChange={(e) => handleInputChange("companyName", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contractor ID"
                value={formData.contractorId}
                onChange={(e) => handleInputChange("contractorId", e.target.value)}
              />
            </Grid>
          </Grid>
        );

      case 1: // Registration Details
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PAN Number *"
                value={formData.panNumber}
                onChange={(e) => handleInputChange("panNumber", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GST Number *"
                value={formData.gstNumber}
                onChange={(e) => handleInputChange("gstNumber", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Business Type</InputLabel>
                <Select
                  value={formData.businessType}
                  onChange={(e) => handleInputChange("businessType", e.target.value)}
                  label="Business Type"
                >
                  {businessTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CLRA RC Number"
                value={formData.rcNumber}
                onChange={(e) => handleInputChange("rcNumber", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Issue Date"
                value={formData.issueDate}
                onChange={(date) => handleInputChange("issueDate", date)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Expiry Date"
                value={formData.expiryDate}
                onChange={(date) => handleInputChange("expiryDate", date)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Issuing Authority"
                value={formData.issuingAuthority}
                onChange={(e) => handleInputChange("issuingAuthority", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Valid States</InputLabel>
                <Select
                  multiple
                  value={formData.validStates}
                  onChange={(e) => handleInputChange("validStates", e.target.value)}
                  label="Valid States"
                >
                  {INDIAN_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 2: // Contact Details
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Primary Contact Name *"
                value={formData.primaryContactName}
                onChange={(e) => handleInputChange("primaryContactName", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Designation *"
                value={formData.primaryContactDesignation}
                onChange={(e) => handleInputChange("primaryContactDesignation", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number *"
                value={formData.primaryContactPhone}
                onChange={(e) => handleInputChange("primaryContactPhone", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={formData.primaryContactEmail}
                onChange={(e) => handleInputChange("primaryContactEmail", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.registeredStreet}
                onChange={(e) => handleInputChange("registeredStreet", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.registeredCity}
                onChange={(e) => handleInputChange("registeredCity", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  value={formData.registeredState}
                  onChange={(e) => handleInputChange("registeredState", e.target.value)}
                  label="State"
                >
                  {INDIAN_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Pincode"
                value={formData.registeredPincode}
                onChange={(e) => handleInputChange("registeredPincode", e.target.value)}
              />
            </Grid>
          </Grid>
        );

      case 3: // Bank Details
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Name *"
                value={formData.accountName}
                onChange={(e) => handleInputChange("accountName", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Number *"
                value={formData.accountNumber}
                onChange={(e) => handleInputChange("accountNumber", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Name *"
                value={formData.bankName}
                onChange={(e) => handleInputChange("bankName", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IFSC Code *"
                value={formData.ifscCode}
                onChange={(e) => handleInputChange("ifscCode", e.target.value)}
                required
              />
            </Grid>
          </Grid>
        );

      case 4: // Owner Details
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Owner Name *"
                value={formData.ownerName}
                onChange={(e) => handleInputChange("ownerName", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Aadhaar Number *"
                value={formData.ownerAadhaarNumber}
                onChange={(e) => handleInputChange("ownerAadhaarNumber", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PAN Number"
                value={formData.ownerPanNumber}
                onChange={(e) => handleInputChange("ownerPanNumber", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.ownerPhoneNumber}
                onChange={(e) => handleInputChange("ownerPhoneNumber", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.ownerEmailAddress}
                onChange={(e) => handleInputChange("ownerEmailAddress", e.target.value)}
              />
            </Grid>
          </Grid>
        );

      case 5: // License Details
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Labor License Number *"
                value={formData.laborLicenseNumber}
                onChange={(e) => handleInputChange("laborLicenseNumber", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>License Type</InputLabel>
                <Select
                  value={formData.licenseType}
                  onChange={(e) => handleInputChange("licenseType", e.target.value)}
                  label="License Type"
                >
                  {licenseTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Issue Date"
                value={formData.licenseIssueDate}
                onChange={(date) => handleInputChange("licenseIssueDate", date)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Valid Till"
                value={formData.licenseValidTill}
                onChange={(date) => handleInputChange("licenseValidTill", date)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Max Workers Allowed"
                type="number"
                value={formData.maxWorkersAllowed}
                onChange={(e) => handleInputChange("maxWorkersAllowed", parseInt(e.target.value) || 0)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>States Applicable</InputLabel>
                <Select
                  multiple
                  value={formData.statesApplicable}
                  onChange={(e) => handleInputChange("statesApplicable", e.target.value)}
                  label="States Applicable"
                >
                  {INDIAN_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 6: // Documents
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Upload Required Documents
              </Typography>
            </Grid>
            {[
              { key: "licenseProof", label: "Labor License Proof *", required: true },
              { key: "agreementProof", label: "Agreement Proof *", required: true },
              { key: "gstCertificate", label: "GST Certificate", required: false },
              { key: "panCard", label: "PAN Card", required: false },
              { key: "aadhaarCard", label: "Aadhaar Card", required: false }
            ].map((doc) => (
              <Grid item xs={12} sm={6} key={doc.key}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      {doc.label}
                    </Typography>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleDocumentUpload(doc.key, e.target.files[0])}
                      style={{ display: "none" }}
                      id={`upload-${doc.key}`}
                    />
                    <label htmlFor={`upload-${doc.key}`}>
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        fullWidth
                      >
                        Choose File
                      </Button>
                    </label>
                    {formData.documents[doc.key] && (
                      <Typography variant="caption" color="success" sx={{ mt: 1, display: "block" }}>
                        ✓ {formData.documents[doc.key].name}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Complete Your Profile</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {loading ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <LinearProgress />
              <Typography sx={{ mt: 2 }}>Loading profile data...</Typography>
            </Box>
          ) : (
            <>
              {/* Progress Overview */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6">Profile Completion</Typography>
                    <Chip 
                      label={`${completionStatus.completionPercentage}%`}
                      color={completionStatus.completionPercentage >= 100 ? "success" : "primary"}
                    />
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={completionStatus.completionPercentage}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  {completionStatus.completionPercentage >= 100 && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CheckIcon />
                        Profile is complete! You can now add workers and access all features.
                      </Box>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Stepper */}
              <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                {steps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {completionStatus.completedSections.includes(
                          ['companyDetails', 'registrationDetails', 'contactDetails', 'bankDetails', 'ownerDetails', 'licenseDetails', 'documents'][index]
                        ) && <CheckIcon fontSize="small" color="success" />}
                        {label}
                      </Box>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Step Content */}
              <Box sx={{ minHeight: 400 }}>
                {renderStepContent(activeStep)}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleBack} 
            disabled={activeStep === 0 || saving}
            startIcon={<BackIcon />}
          >
            Back
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button 
              onClick={handleSave} 
              variant="contained" 
              disabled={saving}
              startIcon={saving ? <LinearProgress size={20} /> : <CheckIcon />}
            >
              {saving ? "Saving..." : "Complete Profile"}
            </Button>
          ) : (
            <Button 
              onClick={handleNext} 
              variant="contained"
              endIcon={<NextIcon />}
            >
              Next
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ContractorProfileWizard;
